<template>
    <!-- Purely decorative: every layer here is aria-hidden, and none of it is
         reachable. The profile's content sits above it on z-index. -->
    <div class="theme-scene" :style="sceneStyles" aria-hidden="true">
        <!-- Rotating rays live in an oversized box so the square's corners
             never sweep into view. A conic gradient can't itself be
             animated; the element holding one can. -->
        <div
            v-if="theme.spinRays"
            class="scene-rays"
            :style="raysStyle"></div>

        <div v-if="theme.scenery" class="scene-ridge" :style="sceneryStyle"></div>

        <!-- Actors and lighting share one clipped box: a faller's whole point
             is that it leaves the panel, and a light shaft sweeps past both
             edges. -->
        <div class="scene-fx">
            <svg
                v-for="(actor, i) in fallers"
                :key="`fall-${i}`"
                class="scene-actor scene-faller"
                :viewBox="actor.viewBox"
                :width="actor.size"
                :height="actor.size"
                :style="actor.style"
                v-html="actor.body"></svg>

            <div v-if="drifter" class="scene-track" :style="drifter.trackStyle">
                <svg
                    class="scene-actor scene-drifter"
                    :viewBox="drifter.viewBox"
                    :width="drifter.width"
                    :height="drifter.height"
                    :style="drifter.style"
                    v-html="drifter.body"></svg>
            </div>

            <template v-for="(pass, i) in lighting">
                <div v-if="pass.kind === 'shaft'" :key="`light-${i}`" class="scene-track" :style="pass.trackStyle">
                    <div class="scene-shaft" :style="pass.style"></div>
                </div>
                <div
                    v-else
                    :key="`light-${i}`"
                    :class="`scene-light is-${pass.kind}`"
                    :style="pass.style"></div>
            </template>
        </div>
    </div>
</template>

<script>
import { SPRITES, rays, ridge, RIDGES, seeded, spriteAspect } from '../../globals/themeSprites.js'

/**
 * Everything decorative behind a profile: lighting, actors and scenery.
 *
 * This is the epic-and-above half of the theme. Below that a theme is a
 * colour and maybe a tiled pattern, both of which are just a `background` on
 * the panel and need no component at all — so for those tiers this renders
 * an empty div and costs nothing.
 *
 * Peekers are *not* here. They used to rise from a horizon line drawn in this
 * layer, which sits behind the cards — so they were being drawn correctly and
 * occluded by the dashboard, and nobody ever saw one. They're mounted by the
 * cards themselves now; see PeekSlot.vue.
 *
 * ── Why it's all CSS ─────────────────────────────────────────────────────
 *
 * Every animation here is a CSS `transform`/`opacity` keyframe, which the
 * compositor can run without waking the main thread. Nothing in this file
 * asks for a frame. That's deliberate and it's the same lesson the page
 * gradient taught: a decorative rAF loop that nobody is watching still costs
 * exactly as much as one that somebody is, and the profile already spends
 * its frame budget on the bubble physics and the reward track.
 *
 * `prefers-reduced-motion` drops all of it — a theme still reads through
 * colour, composition and lighting with nothing moving, because the motion
 * is the flourish and never the content.
 *
 * ── Why the positions are deterministic ──────────────────────────────────
 *
 * Fallers get their left offset, size, duration and spin from a seeded
 * sequence rather than Math.random(). Two reasons: the arrangement is stable
 * across a re-render (a resize would otherwise reshuffle every leaf), and the
 * shop artwork generated from these themes matches what a player actually
 * gets.
 */
export default {
    name: 'ThemeScene',
    props: {
        theme: { type: Object, required: true },
    },
    computed: {
        /**
         * The scene occupies a band at the top of the panel, not the whole of
         * it — see the note on `sceneHeight` in profiles.js. Everything inside
         * positions against this box, so a horizon at 56% is 56% of the
         * *scene*, which is what the theme was composed against.
         */
        sceneStyles() {
            return this.theme.sceneHeight ? { height: `${this.theme.sceneHeight}px` } : null
        },

        raysStyle() {
            const spec = this.theme.spinRays
            return {
                // `.css`, not the layer object. Assigning the object put the
                // text `[object Object]` in backgroundImage, which CSS drops
                // without a word — High Noon's and Sunburst's turning rays
                // simply never drew.
                backgroundImage: rays(spec.color, spec.count, 50, 50).css,
                animationDuration: `${spec.seconds}s`,
            }
        },

        sceneryStyle() {
            const spec = this.theme.scenery
            return {
                backgroundImage: ridge(RIDGES[spec.ridge], spec.color),
                backgroundSize: `100% ${spec.height}`,
            }
        },

        fallers() {
            const spec = this.theme.fall
            if (!spec) return []
            const sprite = SPRITES[spec.sprite]
            if (!sprite) return []

            const random = seeded(spec.sprite)
            const out = []
            for (let i = 0; i < spec.count; i++) {
                const size = spec.min + random() * (spec.max - spec.min)
                const seconds = spec.seconds[0] + random() * (spec.seconds[1] - spec.seconds[0])
                out.push({
                    viewBox: sprite.vb,
                    body: `<g fill="${spec.color}">${sprite.svg}</g>`,
                    size: size.toFixed(1),
                    style: {
                        left: `${(random() * 100).toFixed(1)}%`,
                        animationDuration: `${seconds.toFixed(1)}s`,
                        // Negative delay starts each one already in flight, so
                        // the panel doesn't open on a bare sky that fills in.
                        animationDelay: `${(-random() * seconds).toFixed(1)}s`,
                        opacity: (0.45 + random() * 0.5).toFixed(2),
                        '--drift': `${((random() - 0.5) * spec.drift).toFixed(0)}px`,
                        '--spin': `${(180 + random() * 540).toFixed(0)}deg`,
                    },
                })
            }
            return out
        },

        drifter() {
            const spec = this.theme.drift
            if (!spec) return null
            const sprite = SPRITES[spec.sprite]
            if (!sprite) return null
            return {
                viewBox: sprite.vb,
                body: `<g fill="${spec.color}">${sprite.svg}</g>`,
                width: spec.width,
                height: Math.round(spec.width * spriteAspect(spec.sprite)),
                // Motion belongs to the track, position to the sprite.
                trackStyle: { animationDuration: `${spec.seconds}s` },
                style: { top: spec.top },
            }
        },

        lighting() {
            return (this.theme.light || []).map(pass => {
                if (pass.kind === 'shaft') {
                    return {
                        kind: pass.kind,
                        trackStyle: { animationDuration: `${pass.seconds}s` },
                        style: {
                            width: pass.width,
                            backgroundImage: `linear-gradient(90deg, transparent, ${pass.color}, transparent)`,
                        },
                    }
                }
                if (pass.kind === 'pool') {
                    return { kind: pass.kind, style: {
                        backgroundImage: `radial-gradient(circle at ${pass.x}% ${pass.y}%, ${pass.color} 0%, transparent ${pass.r}%)`,
                        animationDuration: `${pass.seconds}s`,
                    } }
                }
                if (pass.kind === 'flicker') {
                    return { kind: pass.kind, style: { backgroundColor: pass.color } }
                }
                // vignette
                return { kind: pass.kind, style: {
                    backgroundImage: `radial-gradient(ellipse at 50% 40%, transparent 42%, ${pass.color} 100%)`,
                } }
            })
        },
    },
}

</script>

<style scoped lang="scss">
.theme-scene {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    // Height comes from the theme; without one it covers the panel.
    bottom: 0;
    pointer-events: none;
}

.theme-scene[style*='height'] {
    bottom: auto;
}

.scene-rays {
    position: absolute;
    left: -50%;
    top: -50%;
    width: 200%;
    height: 200%;
    animation-name: scene-spin;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}

.scene-ridge {
    position: absolute;
    inset: 0;
    background-repeat: no-repeat;
    background-position: bottom;
}

.scene-fx {
    position: absolute;
    inset: 0;
    overflow: hidden;
}

.scene-actor {
    position: absolute;
    display: block;
}

.scene-faller {
    top: -16%;
    animation-name: scene-fall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}

/**
 * A full-width rail that carries one moving thing across the scene.
 *
 * The moving elements used to animate themselves, and a `translateX`
 * percentage resolves against *the element's own width* — so Skyline's 70px
 * cloud travelled 70px×2.9, never leaving the left corner of a ~1000px panel,
 * and a 16%-wide light shaft swept 16% of that again. Nothing crossed
 * anything. The track is the width of the scene, so -100%→100% is a real
 * edge-to-edge sweep whatever is riding on it.
 */
.scene-track {
    position: absolute;
    inset: 0;
    animation-name: scene-cross;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    will-change: transform;
}

.scene-drifter {
    left: 0;
}

.scene-light {
    position: absolute;
    inset: 0;
}

/**
 * The shaft itself: appearance only, no motion — its track moves it.
 *
 * The skew lives here rather than in the keyframe. It used to be part of
 * `scene-cross`, which meant every sprite sharing that animation was sheared
 * 16° for its whole journey; a leaning shaft is the point, a leaning vulture
 * is a bug.
 */
.scene-shaft {
    position: absolute;
    // Taller than the panel so the skew never exposes a cut-off top or bottom.
    top: -30%;
    bottom: -30%;
    left: 0;
    transform: skewX(-16deg);
    filter: blur(6px);
}

.scene-light.is-pool {
    animation-name: scene-pulse;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
}

.scene-light.is-flicker {
    animation: scene-flicker 4s steps(1, end) infinite;
}

@keyframes scene-fall {
    from { transform: translate3d(0, -20%, 0) rotate(0deg); }
    to   { transform: translate3d(var(--drift, 0px), 780%, 0) rotate(var(--spin, 180deg)); }
}

// Percentages of the *track*, which spans the scene — so this is a true
// edge-to-edge crossing, entering off one side and leaving off the other.
@keyframes scene-cross {
    from { transform: translateX(-100%); }
    to   { transform: translateX(100%); }
}

@keyframes scene-pulse {
    0%, 100% { opacity: 0.30; }
    50%      { opacity: 0.72; }
}

@keyframes scene-flicker {
    0%, 100% { opacity: 0.5; }
    47%      { opacity: 0.5; }
    50%      { opacity: 0.9; }
    53%      { opacity: 0.45; }
    71%      { opacity: 0.85; }
    74%      { opacity: 0.5; }
}

@keyframes scene-spin {
    to { transform: rotate(360deg); }
}

// The composition, the colour and the lighting all survive this — only the
// movement goes. Peekers settle in the up position rather than vanishing,
// so the pigs are still there, just not popping.
@media (prefers-reduced-motion: reduce) {
    .scene-rays,
    .scene-faller,
    .scene-track,
    .scene-light {
        animation: none !important;
    }
}
</style>
